import React, { useEffect, useMemo, useState } from "react";
import styles from "../Home/HomePage.module.css";
const authenticatedFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token) {
    window.location.href = '/#/login';
    throw new Error('Authentication required');
  }
  
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
    },
  });
};


export default function MyNotificationsPage() {
  localStorage.setItem("navbar_state", "user");

  const API = "https://simplix.co.za/api";
  //const API = "http://127.0.0.1:8000/api";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [q, setQ] = useState("");
  const [viaFilter, setViaFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  const profile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("profile") || "{}");
    } catch {
      return {};
    }
  }, []);

const loadNotifications = async () => {
  try {
    // No user identifiers exposed in URL!
    const res = await authenticatedFetch(`${API}/notifications/my/`);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error || "Could not load notifications.");
    setNotifications(json.notifications || []);
  } catch (e) {
    setError(e.message);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function formatDate(value) {
    if (!value) return "—";
    return new Date(value).toLocaleString();
  }

  function getViaLabel(via) {
    const v = String(via || "").toLowerCase();
    if (v.includes("whatsapp") || v === "100000001") return "WhatsApp";
    if (v.includes("email") || v === "539270001") return "Email";
    return via || "Other";
  }

  function splitMessage(message) {
    return String(message || "")
      .split(/\n+/)
      .map((x) => x.trim())
      .filter(Boolean);
  }

  function normalizeMessage(message) {
    return String(message || "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  }

  function mergeChannels(items) {
    const map = new Map();

    for (const n of items) {
      const key = normalizeMessage(n.message);

      if (!key) continue;

      const existing = map.get(key);

      if (!existing) {
        map.set(key, {
          ...n,
          channels: [getViaLabel(n.sentVia)],
          sentTos: [n.sentTo].filter(Boolean),
          sentToNumbers: [n.sentToNumber].filter(Boolean),
        });
      } else {
        const channel = getViaLabel(n.sentVia);

        existing.channels = Array.from(
          new Set([...existing.channels, channel]),
        );

        existing.sentTos = Array.from(
          new Set([...existing.sentTos, n.sentTo].filter(Boolean)),
        );

        existing.sentToNumbers = Array.from(
          new Set([...existing.sentToNumbers, n.sentToNumber].filter(Boolean)),
        );

        if (
          n.createdOn &&
          existing.createdOn &&
          new Date(n.createdOn) > new Date(existing.createdOn)
        ) {
          existing.createdOn = n.createdOn;
        }
      }
    }

    return Array.from(map.values());
  }

  const mergedNotifications = useMemo(() => {
    return mergeChannels(notifications);
  }, [notifications]);

  const viaOptions = useMemo(() => {
    const set = new Set();

    mergedNotifications.forEach((n) => {
      (n.channels || [getViaLabel(n.sentVia)]).forEach((channel) =>
        set.add(channel),
      );
    });

    return ["all", ...Array.from(set).sort()];
  }, [mergedNotifications]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();

    return mergedNotifications.filter((n) => {
      const channels = n.channels || [getViaLabel(n.sentVia)];
      const createdDate = n.createdOn
        ? new Date(n.createdOn).toISOString().slice(0, 10)
        : "";

      const matchesSearch =
        !term ||
        [
          ...(n.sentTos || []),
          ...(n.sentToNumbers || []),
          ...channels,
          n.message,
          n.createdOn,
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      const matchesVia = viaFilter === "all" || channels.includes(viaFilter);

      const matchesDate = !dateFilter || createdDate === dateFilter;

      return matchesSearch && matchesVia && matchesDate;
    });
  }, [q, viaFilter, dateFilter, mergedNotifications]);

  function clearFilters() {
    setQ("");
    setViaFilter("all");
    setDateFilter("");
  }

  return (
    <div className={styles.page}>
      <div className={styles.section}>
        <div className={styles.meterDetailHero}>
          <div>
            <p className={styles.sectionEyebrow}>Notifications</p>
            <h1 className={styles.h2}>My Notifications</h1>
            <p className={styles.sectionIntro}>
              View all notifications sent to you.
            </p>
          </div>

          <button
            className={styles.inlineBtn}
            type="button"
            onClick={loadNotifications}
          >
            Refresh
          </button>
        </div>

        <div className={styles.meterDetailPanel}>
          <div className={styles.meterDetailPanelHeader}>
            <div>
              <p className={styles.sectionEyebrow}>History</p>
              <h2>Sent notifications</h2>
              <p className={styles.meterPanelText}>
                Showing {filtered.length} of {mergedNotifications.length}
              </p>
            </div>
          </div>

          <div className={styles.notificationFilters}>
            <input
              className={styles.authInput}
              placeholder="Search by message, number, email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

            <select
              className={styles.authInput}
              value={viaFilter}
              onChange={(e) => setViaFilter(e.target.value)}
            >
              {viaOptions.map((v) => (
                <option key={v} value={v}>
                  {v === "all" ? "All channels" : v}
                </option>
              ))}
            </select>

            <input
              className={styles.authInput}
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />

            <button
              className={styles.secondaryBtn}
              type="button"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>

          {loading && (
            <div className={styles.meterLoadingBox}>Loading notifications…</div>
          )}
          {error && <div className={styles.noteBox}>{error}</div>}

          {!loading && !error && (
            <div className={styles.notificationList}>
              {filtered.map((n) => {
                const channels = n.channels || [getViaLabel(n.sentVia)];
                const lines = splitMessage(n.message);

                return (
                  <article key={n.id} className={styles.notificationCard}>
                    <div className={styles.notificationCardTop}>
                      <div>
                        <span className={styles.notificationBadge}>
                          {channels.join(" + ")}
                        </span>
                        <h3>{lines[0] || "Notification"}</h3>
                      </div>

                      <span className={styles.notificationDate}>
                        {formatDate(n.createdOn)}
                      </span>
                    </div>

                    <div className={styles.notificationMeta}>
                      <span>
                        Sent to:{" "}
                        {(n.sentTos || [n.sentTo]).filter(Boolean).join(", ") ||
                          "—"}
                      </span>
                      <span>
                        {(n.sentToNumbers || [n.sentToNumber])
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </span>
                    </div>

                    <div className={styles.notificationMessage}>
                      {lines.slice(1).map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>
                  </article>
                );
              })}

              {!filtered.length && (
                <div className={styles.noteBox}>No notifications found.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
